import { RupeeIcon } from 'core/components/Icons'
import { Column, Table } from 'core/components/ui/Table'
import { RecoveryRow } from '../../../domain/entities/SalesReport'
import { SectionCard } from './SectionCard'
import { formatCurrency, PAIRED_TABLE_BODY_HEIGHT } from './format'

interface MoneyRecoveredTableProps {
  rows: RecoveryRow[]
  loading?: boolean
}

const getPendingAmountClass = (row: RecoveryRow): string => {
  if (row.isTotal) {
    return 'font-extrabold text-danger'
  }

  if (row.pendingAmount > 0) {
    return 'text-danger font-medium'
  }

  return 'text-text-secondary'
}

export function MoneyRecoveredTable({ rows, loading = false }: MoneyRecoveredTableProps) {
  const columns: Column<RecoveryRow>[] = [
    {
      key: 'entityName',
      label: 'FPO',
      render: (row) => (
        <span className={row.isTotal ? 'font-extrabold text-text-primary' : 'font-semibold text-text-primary'}>
          {row.entityName}
        </span>
      ),
    },
    {
      key: 'totalSaleAmount',
      label: 'Total Sale',
      render: (row) => (
        <span className={row.isTotal ? 'font-extrabold text-text-primary' : 'text-text-primary'}>
          {formatCurrency(row.totalSaleAmount)}
        </span>
      ),
    },
    {
      key: 'receivedAmount',
      label: 'Received',
      render: (row) => (
        <span className={row.isTotal ? 'font-extrabold text-status-active' : 'text-status-active font-medium'}>
          {formatCurrency(row.receivedAmount)}
        </span>
      ),
    },
    {
      key: 'pendingAmount',
      label: 'Pending',
      render: (row) => <span className={getPendingAmountClass(row)}>{formatCurrency(row.pendingAmount)}</span>,
    },
    {
      key: 'recovery',
      label: 'Recovery',
      className: 'min-w-[120px]',
      render: (row) => {
        const pct = row.totalSaleAmount > 0 ? Math.round((row.receivedAmount / row.totalSaleAmount) * 100) : 0
        return (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-border/40">
              <div
                className={`h-full rounded-full ${pct >= 100 ? 'bg-status-active' : 'bg-primary'}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-text-secondary">{pct}%</span>
          </div>
        )
      },
    },
  ]

  return (
    <SectionCard title="Money Recovered" subtitle="Sale amount collected vs. outstanding, by FPO">
      <Table
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="No recovery records."
        emptyIcon={<RupeeIcon size={20} className="opacity-60" />}
        bodyHeight={PAIRED_TABLE_BODY_HEIGHT}
        stickyLastRow
      />
    </SectionCard>
  )
}
